import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IEventLabelEntityAttributes extends IModelAttributes {
	label: string;
	iconName: string;
	colour: string;
	hidden: boolean;

	eventDetails: Array<Models.EventDetailEntity | Models.IEventDetailEntityAttributes>;
}
@entity('EventLabelEntity', 'Event Label')
export default class EventLabelEntity extends Model implements IEventLabelEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The label that will appear with this event.
	 */
	@observable
	@attribute<EventLabelEntity, string>()
	public label: string;

	/**
	 * The Material Symbols icon name used for the event label.
	 */
	@observable
	@attribute<EventLabelEntity, string>()
	public iconName: string = 'local_bar';

	/**
	 * The colour that this event will use.
	 */
	@observable
	@attribute<EventLabelEntity, string>()
	public colour: string = '#000000';

	/**
	 * Whether or not this event label will cause the event to be private.
	 */
	@observable
	@attribute<EventLabelEntity, boolean>()
	public hidden: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public eventDetails: Models.EventDetailEntity[] = [];

	constructor(attributes?: Partial<IEventLabelEntityAttributes>) {
		super(attributes);

		this.eventDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IEventLabelEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.label !== undefined) {
				this.label = attributes.label;
			}
			if (attributes.iconName !== undefined) {
				this.iconName = attributes.iconName;
			}
			if (attributes.colour !== undefined) {
				this.colour = attributes.colour;
			}
			if (attributes.hidden !== undefined) {
				this.hidden = attributes.hidden;
			}
			if (attributes.eventDetails !== undefined && Array.isArray(attributes.eventDetails)) {
				if (!this.eventDetails) {
					this.eventDetails = [];
				}
				for (const model of attributes.eventDetails) {
					if (model instanceof Models.EventDetailEntity) {
						this.eventDetails.push(model);
					} else {
						this.eventDetails.push(new Models.EventDetailEntity(model));
					}
				}
			}
		}
	}
}
